import React, {useState, useContext, useMemo, useCallback, useEffect} from 'react';
import {
  Keyboard,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {FontAwesome} from '@expo/vector-icons';
import * as Sentry from 'sentry-expo';

import {SettingsContext} from '../../contexts/SettingsContext';
import {ThemeContext} from '../../contexts/ThemeContext';
import {UserContext} from '../../contexts/UserContext';
import Api from '../../constants/Api';
import OnboardingLayout from './components/OnboardingLayout';
import StyledButton from '../../components/StyledButton';
import countries from '../../assets/countries';
import languageList from '../../assets/languages-list';
import SearchModal from '../../components/SearchModal';

const DEFAULT_LANGUAGE = languageList.find(language => language.code === 'en-US');
const DEFAULT_COUNTRY = countries.find(({code}) => code === 'US');

const getLanguageObject = code => languageList.find(language => code.length === 2
  ? code === language.code.slice(0, 2)
  : code === language.code
) || DEFAULT_LANGUAGE;

const LanguageScreen = ({navigation}) => {
  const {t} = useTranslation();
  const {settings, updateSettings} = useContext(SettingsContext);
  const {setUser, userId, userToken} = useContext(UserContext);
  const theme = useContext(ThemeContext);
  const styles = useMemo(() => getStyles(theme), [theme]);

  const [loading, setLoading] = useState(false);

  const [languageModalOpen, setLanguageModalOpen] = useState(false);

  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [countryModalOpen, setCountryModalOpen] = useState(false);

  useEffect(() => {
    const initCountry = async () => {
      let countryCode;
      let locale = settings.language;

      if (Platform.OS === 'web') {
        // eslint-disable-next-line no-undef
        if (Intl && Intl.DateTimeFormat) {
          // eslint-disable-next-line no-undef
          locale = Intl.DateTimeFormat().resolvedOptions().locale;
        }
      }

      const splitted = locale.split('-');

      countryCode = splitted.length > 1 ? splitted[1] : locale;

      if (countryCode) {
        setCountry(countries.find(({code}) => code === countryCode.toUpperCase()) || DEFAULT_COUNTRY);
      }
    };

    initCountry();
  }, []);

  const openLanguageModal = useCallback(() => {
    setLanguageModalOpen(true);
  }, [setLanguageModalOpen]);

  const closeLanguageModal = useCallback(() => {
    setLanguageModalOpen(false);
  }, [setLanguageModalOpen]);

  const handleChangeLanguage = useCallback(newLanguage => {
    updateSettings({...settings, language: newLanguage.code});
  }, [settings, updateSettings]);

  const openCountryModal = useCallback(() => {
    setCountryModalOpen(true);
  }, [setCountryModalOpen]);

  const closeCountryModal = useCallback(() => {
    setCountryModalOpen(false);
  }, [setCountryModalOpen]);

  const handlePress = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${Api.apiBaseUrl}/users/${userId}`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_token: userToken,
          language: settings.language,
          country_code: country.code,
        }),
      });

      const responseJson = await response.json();

      setLoading(false);

      if (responseJson) {
        setUser(responseJson);
        navigation.navigate('Name', {onboarding: true});
      }

      return responseJson;
    } catch (error) {
      // TODO: Error handling

      if (Platform.OS === 'web') {
        Sentry.Browser.captureException(error);
      } else {
        Sentry.Native.captureException(error);
      }
    }
  };

  const language = useMemo(() => getLanguageObject(settings.language), [settings.language]);

  return (
    <OnboardingLayout
      footer={
        <StyledButton
          text={t('OnboardingLayout.continue', 'Continue →')}
          loading={loading}
          onPress={handlePress}
        />
      }
    >
      <Text style={{color: theme.textPrimary, fontSize: 16}}>
        {t('LanguageScreen.fields.language.label', 'Language')}
      </Text>

      <View style={styles.inputContainer}>
        <TouchableOpacity
          style={{flex: 1}}
          onPress={() => {
            if (Platform.OS !== 'web') {
              Keyboard.dismiss();
            }
            openLanguageModal();
          }}
          accessible={false}
        >
          <View style={{flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'}}>
            <Text style={styles.flag}>
              {language.flag}
            </Text>

            <Text style={{color: theme.textPrimary, fontSize: 16}}>
              {language.name}
            </Text>

            <FontAwesome
              style={[styles.iconStyle, {marginLeft: 0, marginRight: 10}]}
              name="caret-down"
              size={28}
            />
          </View>
        </TouchableOpacity>

        <SearchModal
          data={languageList}
          open={languageModalOpen}
          close={closeLanguageModal}
          onClick={handleChangeLanguage}
        />
      </View>

      <Text style={{color: theme.textPrimary, fontSize: 16}}>
        {t('LanguageScreen.fields.country.label', 'Country')}
      </Text>

      <View style={styles.inputContainer}>
        <TouchableOpacity
          style={{flex: 1}}
          onPress={() => {
            if (Platform.OS !== 'web') {
              Keyboard.dismiss();
            }
            openCountryModal();
          }}
          accessible={false}
        >
          <View style={{flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'}}>
            <Text style={styles.flag}>
              {country.flag}
            </Text>

            <Text style={{color: theme.textPrimary, fontSize: 16}}>
              {country.name}
            </Text>

            <FontAwesome
              style={[styles.iconStyle, {marginLeft: 0, marginRight: 10}]}
              name="caret-down"
              size={28}
            />
          </View>
        </TouchableOpacity>

        <SearchModal
          data={countries}
          open={countryModalOpen}
          close={closeCountryModal}
          onClick={item => {
            setCountry(item);
          }}
          getText={item => item.name}
        />
      </View>
    </OnboardingLayout>
  );
};

const getStyles = theme => StyleSheet.create({
  inputContainer: {
    flexDirection: 'row',
    width: 320,
    marginTop: 15,
    marginBottom: 25,
    padding: 10,
    borderRadius: 50,
    backgroundColor: theme.inputBackgroundColor,
    borderColor: theme.inputBorderColor,
  },
  flag: {
    fontSize: 28,
    marginLeft: 5,
    marginRight: 5,
  },
  iconStyle: {
    color: theme.colorSecondary,
    marginTop: 5,
    marginBottom: 0,
  },
});

export default LanguageScreen;
